import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';

export interface StatusItem {
  valueId: string;
  valueKey: string;
  valueName: string;
  notificationLevel: 'error' | 'warning' | 'alert';
  notificationMessage: string | null;
  currentValue: unknown;
  lastUpdated: string | null;
  deviceId: string;
  deviceName: string;
  deviceType: string | null;
  roomId: string | null;
}

export interface EventLogEntry {
  id: string;
  eventType: string;
  severity: string;
  message: string;
  deviceId?: string;
  createdAt: string;
}

export const useSystemStatusStore = defineStore('systemStatus', () => {
  const socketStore = useSocketStore();

  // State
  const statusItems = ref<StatusItem[]>([]);
  const eventLog = ref<EventLogEntry[]>([]);
  const loadingEvents = ref(false);
  const initialized = ref(false);
  let _initPromise: Promise<void> | null = null;
  let _eventsOffset = 0;

  // Computed
  const errorCount = computed(() => statusItems.value.filter((i) => i.notificationLevel === 'error').length);
  const warningCount = computed(() => statusItems.value.filter((i) => i.notificationLevel === 'warning').length);
  const alertCount = computed(() => statusItems.value.filter((i) => i.notificationLevel === 'alert').length);
  const totalCount = computed(() => statusItems.value.length);
  const highestSeverity = computed<'error' | 'warning' | 'alert' | 'none'>(() => {
    if (errorCount.value > 0) return 'error';
    if (warningCount.value > 0) return 'warning';
    if (alertCount.value > 0) return 'alert';
    return 'none';
  });

  // Actions
  async function fetchStatusValues() {
    try {
      const data = await apiFetch('/devices/status-values');
      statusItems.value = Array.isArray(data) ? data : [];
    } catch (e) {
      console.error('[systemStatus] Failed to fetch status values', e);
    }
  }

  async function fetchEvents(reset = true) {
    loadingEvents.value = true;
    try {
      if (reset) _eventsOffset = 0;
      const data = await apiFetch(`/events?limit=20&offset=${_eventsOffset}`);
      if (reset) {
        // Guard against malformed responses that would turn eventLog into a
        // non-array and crash the socket event handler (unshift is not a fn).
        eventLog.value = Array.isArray(data.events) ? data.events : Array.isArray(data) ? data : [];
      } else if (Array.isArray(data.events) || Array.isArray(data)) {
        eventLog.value.push(...(Array.isArray(data.events) ? data.events : data));
      }
      _eventsOffset = eventLog.value.length;
    } catch (e) {
      console.error('[systemStatus] Failed to fetch events', e);
    } finally {
      loadingEvents.value = false;
    }
  }

  async function loadMoreEvents() {
    await fetchEvents(false);
  }

  function _handleDeviceValueChanged(payload: { deviceId: string; valueKey: string; value: unknown }) {
    // Check if this value is in our status items
    const idx = statusItems.value.findIndex((i) => i.deviceId === payload.deviceId && i.valueKey === payload.valueKey);
    if (idx !== -1) {
      const val = payload.value;
      const isFalsy =
        val === null || val === undefined || val === false || val === 0 || val === '' || val === 'false' || val === '0';
      if (isFalsy) {
        // Value became falsy → remove from active status
        statusItems.value.splice(idx, 1);
      } else {
        // Update the current value
        statusItems.value[idx].currentValue = val;
        statusItems.value[idx].lastUpdated = new Date().toISOString();
      }
    } else {
      // Not currently tracked — re-fetch to pick up new alerts
      // Debounce: only re-fetch if we haven't recently
      _debouncedRefetch();
    }
  }

  let _refetchTimer: ReturnType<typeof setTimeout> | null = null;
  function _debouncedRefetch() {
    if (_refetchTimer) return;
    _refetchTimer = setTimeout(() => {
      _refetchTimer = null;
      fetchStatusValues();
    }, 3000);
  }

  function _registerSocketListeners() {
    const s = socketStore.socket;
    if (!s) return;
    s.off('device_value_changed', _handleDeviceValueChanged);
    s.on('device_value_changed', _handleDeviceValueChanged);
    s.off('system_event', _handleSystemEvent);
    s.on('system_event', _handleSystemEvent);
  }

  function _handleSystemEvent(entry: EventLogEntry) {
    // Guard against a non-array eventLog (e.g. a malformed /events response).
    if (!Array.isArray(eventLog.value)) eventLog.value = [];
    // Prepend to event log so newest is at top
    eventLog.value.unshift(entry);
    // Keep list bounded (drop extras beyond the initial page size)
    if (eventLog.value.length > 100) eventLog.value.length = 100;
    // A new event may change the active status items — refresh them, but
    // rate-limited: bursts of system events (or a socket reconnect replaying
    // them) must not flood the hub with repeated /devices/status-values calls.
    _debouncedRefetch();
  }

  async function initialize() {
    if (initialized.value) return;
    if (_initPromise) return _initPromise;
    _initPromise = (async () => {
      try {
        await Promise.all([fetchStatusValues(), fetchEvents()]);
        initialized.value = true;
        _registerSocketListeners();
        watch(
          () => socketStore.socket,
          (s) => {
            if (s) _registerSocketListeners();
          },
        );
      } finally {
        _initPromise = null;
      }
    })();
    return _initPromise;
  }

  return {
    statusItems,
    eventLog,
    loadingEvents,
    initialized,
    errorCount,
    warningCount,
    alertCount,
    totalCount,
    highestSeverity,
    fetchStatusValues,
    fetchEvents,
    loadMoreEvents,
    initialize,
  };
});
